// =========================================================================
// UNIFI INSTALLATION — GOOGLE ADS LANDING PAGE (/unifi-installer)
// =========================================================================
//
// Conversion-focused landing page for high-intent searches like
// "unifi installer tampa". Follows the ManagedItServicesLandingPage recipe
// from SightlineWebsite.tsx: one primary action (request a consultation),
// a headline that message-matches the ad copy, and the shared landing-page
// kit for hero, trust signals, forms, and tracking.
//
// IMPORTANT (brand/legal): SightLine Networks is an independent service
// provider. We install and support Ubiquiti equipment but are not affiliated
// with, endorsed by, or an authorized partner of Ubiquiti Inc. Copy on this
// page must never claim certification or partnership — see the disclaimer
// rendered near the bottom of the page.

import { useT } from "../i18n";
import type { PageKey } from "../i18n";
import {
  LeadForm,
  MobileStickyCta,
  ServiceHero,
  TrustBar,
  IndustryGrid,
  WhyChooseUs,
  ServiceArea,
  LeadCta,
  SectionLabel,
  FeatureCard,
  PageLink,
  trackCta,
} from "../SightlineWebsite";
import {
  Building2,
  Camera,
  Cctv,
  ChevronRight,
  GitBranch,
  Headphones,
  MapPin,
  Network,
  Router,
  Server,
  Settings,
  UserCheck,
  Wifi,
  Wrench,
  Zap,
} from "lucide-react";

/** FAQ copy for the visible accordion AND the FAQPage JSON-LD for this page. */
export const unifiFaqs: Array<{ q: string; a: string }> = [
  {
    q: "Can you fix an existing UniFi network?",
    a: "Yes. Most problems — weak coverage, dropped clients, misconfigured VLANs, or devices sitting outside a controller — can be diagnosed and fixed without replacing equipment. We start by surveying what you have and tell you what is worth keeping.",
  },
  {
    q: "Do you install access points?",
    a: "Yes. We survey the building, plan AP placement for full coverage, mount and cable the access points, and configure them in a controller so roaming and guest isolation work the way they should.",
  },
  {
    q: "Is UniFi better than consumer Wi-Fi for a business?",
    a: "For a business, usually yes. UniFi gives you centralized management, VLAN segmentation, guest isolation, and PoE-powered access points built for continuous use — things consumer routers and mesh kits generally do not handle well.",
  },
  {
    q: "Can you add cameras to an existing UniFi Protect setup?",
    a: "Yes. If the Protect controller or NVR has recording capacity available, we can mount and aim new cameras and set up recording schedules. If the existing hardware is maxed out, we will explain what an upgrade would involve before you spend anything.",
  },
  {
    q: "Do you work on networks you didn't install?",
    a: "Yes. We routinely take over UniFi networks installed by other vendors or assembled in-house, adopt the existing equipment into a controller, and clean up the configuration from there.",
  },
  {
    q: "Do you provide ongoing UniFi support?",
    a: "Yes. Ongoing support covers monitoring, firmware updates, and configuration changes as your needs change. Many clients start with a one-time project and add support later; that works fine.",
  },
];

export function UnifiInstallerLanding({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();

  const scrollToLeadForm = () => {
    document
      .getElementById("unifi-installer-lead-form")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const services: Array<{ icon: typeof Wifi; title: string; text: string }> = [
    {
      icon: Wifi,
      title: "UniFi Access Points",
      text: "Ceiling and wall-mounted access points, positioned and configured for full coverage without dead zones.",
    },
    {
      icon: Network,
      title: "UniFi Switches",
      text: "PoE switching sized for your access points, cameras, and desktops, with clean rack organization.",
    },
    {
      icon: Router,
      title: "UniFi Gateways",
      text: "Dream Machine and Cloud Gateway installations with firewall rules, WAN failover, and remote access set up correctly.",
    },
    {
      icon: Cctv,
      title: "UniFi Protect Cameras",
      text: "Protect camera and NVR installations with sensible recording settings, storage planning, and remote viewing.",
    },
    {
      icon: Server,
      title: "Network Controllers",
      text: "Self-hosted or cloud controllers so every device is adopted, monitored, and upgradable from one place.",
    },
    {
      icon: GitBranch,
      title: "VLAN Configuration",
      text: "Separated networks for staff, guests, cameras, and phones so traffic stays isolated and secure.",
    },
    {
      icon: UserCheck,
      title: "Guest Wi-Fi",
      text: "Isolated guest networks with custom portals and bandwidth limits that keep visitors off your business network.",
    },
    {
      icon: Wrench,
      title: "Troubleshooting & Optimization",
      text: "Diagnosis and fixes for slow Wi-Fi, dropped clients, roaming problems, and misconfigured devices.",
    },
  ];

  const scenarios: Array<{ icon: typeof Server; title: string; text: string }> = [
    {
      icon: Server,
      title: "New installations",
      text: "Complete design and installation for offices, warehouses, and retail spaces — site survey, AP and camera placement, cabling coordination, and a configured, tested network at handoff.",
    },
    {
      icon: Wrench,
      title: "Existing system troubleshooting",
      text: "Slow Wi-Fi, dropped clients, misconfigured VLANs, and bad AP placement are all fixable. We find the real cause instead of replacing hardware on a guess.",
    },
    {
      icon: Zap,
      title: "Upgrades & expansion",
      text: "Add access points, replace consumer-grade gear, extend UniFi Protect cameras, or adopt orphaned devices into a controller so your network grows without a rebuild.",
    },
  ];

  const steps: Array<{ title: string; text: string }> = [
    {
      title: "On-site survey",
      text: "We walk the building, check cable runs and mounting options, and identify dead zones and interference sources.",
    },
    {
      title: "Network design",
      text: "You receive a clear proposal: equipment list, AP and camera placement, VLAN plan, and pricing.",
    },
    {
      title: "Installation & configuration",
      text: "Mounting, cabling, adoption into the controller, VLANs, firewall rules, and Wi-Fi networks configured.",
    },
    {
      title: "Testing & handoff",
      text: "We test coverage, roaming, cameras, and failover on-site, then walk you through the dashboard.",
    },
    {
      title: "Ongoing support available",
      text: "Monitoring, firmware updates, and changes as your business grows — optional, never forced.",
    },
  ];

  return (
    // Bottom padding on mobile only, clearing the sticky call/consult bar.
    <main className="pb-24 lg:pb-0">
      <ServiceHero
        eyebrow="Serving Tampa Bay businesses"
        title="Professional UniFi Installation & Support in Tampa Bay"
        subtitle="We design, install, and support UniFi networks for businesses — access points, switching, gateways, and UniFi Protect cameras, configured properly from day one."
        primaryLabel="Request a UniFi Consultation"
        onPrimary={() => {
          trackCta("unifi_consultation_click", "unifi_hero", "unifi-installer");
          scrollToLeadForm();
        }}
        ctaName="unifi-hero"
        page="unifi-installer"
      />

      <TrustBar
        items={[
          { icon: MapPin, label: "Local Tampa Bay Support" },
          { icon: Wrench, label: "New Installs & Existing Systems" },
          { icon: Network, label: "Network, Wi-Fi & Cameras" },
          { icon: Building2, label: "Business-Focused Design" },
        ]}
      />

      {/* What we install & support */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <SectionLabel>{t("What we install & support")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("UniFi Equipment We Install and Support")}
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            {t(
              "From the gateway at the edge of your network to the cameras on your wall, we work with the UniFi line every week — and we know where these deployments typically go wrong."
            )}
          </p>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map(({ icon, title, text }) => (
            <FeatureCard key={title} icon={icon} title={t(title)} text={t(text)} />
          ))}
        </div>
      </section>

      {/* Common scenarios */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <SectionLabel>{t("Common scenarios")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Starting Fresh, Fixing Something, or Expanding")}
            </h2>
          </div>
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {scenarios.map(({ icon, title, text }) => (
              <FeatureCard key={title} icon={icon} title={t(title)} text={t(text)} />
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <SectionLabel>{t("How we work")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("A Straightforward Process, Start to Finish")}
          </h2>
        </div>
        <ol className="mx-auto mt-12 max-w-3xl space-y-6">
          {steps.map(({ title, text }, index) => (
            <li key={title} className="flex gap-4">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-600 text-sm font-semibold text-white">
                {index + 1}
              </span>
              <div>
                <h3 className="text-base font-semibold text-slate-900">{t(title)}</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{t(text)}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Internal links to related service pages */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <div className="mx-auto max-w-2xl text-center">
            <SectionLabel>{t("Related services")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Explore Related Services")}
            </h2>
          </div>
          <div className="mx-auto mt-8 grid max-w-3xl gap-4 sm:grid-cols-3">
            <PageLink
              to="camera-installation"
              setPage={setPage}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm font-medium text-slate-700 shadow-sm transition hover:border-sky-200 hover:text-sky-700"
            >
              {t("Add UniFi Protect cameras")}
              <ChevronRight className="h-4 w-4 text-sky-600" />
            </PageLink>
            <PageLink
              to="business-wifi"
              setPage={setPage}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm font-medium text-slate-700 shadow-sm transition hover:border-sky-200 hover:text-sky-700"
            >
              {t("Fix slow business Wi-Fi")}
              <ChevronRight className="h-4 w-4 text-sky-600" />
            </PageLink>
            <PageLink
              to="structured-cabling"
              setPage={setPage}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm font-medium text-slate-700 shadow-sm transition hover:border-sky-200 hover:text-sky-700"
            >
              {t("Structured cabling for new drops")}
              <ChevronRight className="h-4 w-4 text-sky-600" />
            </PageLink>
          </div>
        </div>
      </section>

      {/* Who we help */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <SectionLabel>{t("Who we help")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("UniFi for Businesses Across Tampa Bay")}
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            {t(
              "We design and support UniFi networks for organizations that want business-grade reliability without enterprise complexity."
            )}
          </p>
        </div>
        <div className="mt-10">
          <IndustryGrid
            items={[
              "Offices",
              "Warehouses",
              "Retail",
              "Restaurants",
              "Multi-Floor Buildings",
              "Professional Offices",
            ]}
          />
        </div>
      </section>

      {/* Why SightLine */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <SectionLabel>{t("Why SightLine Networks")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Why Businesses Choose SightLine Networks")}
            </h2>
          </div>
          <div className="mt-12">
            <WhyChooseUs
              items={[
                {
                  icon: MapPin,
                  title: "Local Tampa Bay Support",
                  text: "Work with a local team that knows your network — not a ticket queue in another time zone.",
                },
                {
                  icon: Settings,
                  title: "Practical Network Design",
                  text: "Networks designed around how your building and your team actually work, not a one-size-fits-all template.",
                },
                {
                  icon: Building2,
                  title: "Business-Focused Solutions",
                  text: "Every recommendation is made for a business environment, not a residential setup.",
                },
                {
                  icon: Headphones,
                  title: "Responsive Service",
                  text: "When something needs attention, you reach a person who already knows your setup.",
                },
              ]}
            />
          </div>
        </div>
      </section>

      {/* Real work / projects */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <SectionLabel>{t("Project spotlights")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("Recent UniFi Work")}
          </h2>
          <p className="mt-3 text-sm italic text-slate-500">
            {t("Project spotlights coming soon — real Tampa Bay project photos and details will be added here.")}
          </p>
        </div>
        {/*
          PLACEHOLDER — TODO(marketing): replace these tiles with real UniFi
          project photos (access point mounts, rack builds, Protect camera
          installs, controller dashboards) once supplied. Do not fill with
          stock imagery — see the handoff notes for what to send over.
        */}
        <div className="mx-auto mt-10 grid max-w-2xl gap-5 sm:grid-cols-2">
          {[
            { icon: Camera, label: "UniFi Protect Camera Installation" },
            { icon: Wifi, label: "Multi-AP Office Wi-Fi Deployment" },
          ].map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center"
            >
              <Icon className="h-7 w-7 text-slate-400" />
              <span className="text-sm font-medium text-slate-500">{t(label)}</span>
              <span className="text-xs uppercase tracking-wide text-slate-400">
                {t("Photo coming soon")}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Service area */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20 text-center">
          <SectionLabel>{t("Service area")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("Serving Businesses Throughout Tampa Bay")}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-600 leading-relaxed">
            {t("SightLine Networks installs and supports UniFi networks throughout the Tampa Bay area, including:")}
          </p>
          <div className="mt-8">
            <ServiceArea cities={["Tampa", "Clearwater", "St. Petersburg", "Largo", "Pinellas Park", "Brandon"]} />
          </div>
        </div>
      </section>

      <LeadCta
        title="Ready for a properly configured UniFi network?"
        text="Tell us about your building, your current network, and what is not working. We will follow up with a clear next step."
        primaryLabel="Request a UniFi Consultation"
        onPrimary={() => {
          trackCta("unifi_consultation_click", "unifi_bottom", "unifi-installer");
          scrollToLeadForm();
        }}
        ctaName="unifi-bottom"
        page="unifi-installer"
      />

      {/* Lead form */}
      <section id="unifi-installer-lead-form" className="mx-auto max-w-3xl scroll-mt-20 px-6 py-20">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="text-center">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {t("Request Your UniFi Consultation")}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {t("Fill out the form and our Tampa Bay team will follow up within one business day.")}
            </p>
          </div>
          <div className="mt-8">
            <LeadForm
              page="unifi-installer"
              source="unifi-installer-landing"
              formId="unifi-installer-form"
              subject="UniFi"
              submitLabel="Request a UniFi Consultation"
            />
          </div>
        </div>
      </section>

      {/* FAQ — same native details/summary accordion as the Home page.
          unifiFaqs (exported above) also feeds this page's FAQPage JSON-LD,
          so the two can never drift apart. */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-4xl px-6 py-20">
          <div className="mb-10 max-w-2xl">
            <SectionLabel>{t("Frequently asked questions")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Common UniFi Questions")}
            </h2>
          </div>
          <div className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
            {unifiFaqs.map(({ q, a }) => (
              <details key={q} className="group p-6">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold text-slate-900 marker:hidden">
                  <span>{t(q)}</span>
                  <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-sky-600 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{t(a)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Independent-provider disclaimer — required by our brand/legal rules.
          Visible, small, muted; not buried in a footer. */}
      <p className="mx-auto max-w-3xl px-6 pb-16 text-center text-xs leading-relaxed text-slate-400">
        {t(
          "SightLine Networks is an independent service provider. We install and support Ubiquiti equipment, but we are not affiliated with, endorsed by, or an authorized partner of Ubiquiti Inc. Ubiquiti, UniFi, and UniFi Protect are trademarks of Ubiquiti Inc."
        )}
      </p>

      <MobileStickyCta
        onConsultClick={() => {
          trackCta("unifi_consultation_click", "unifi_sticky", "unifi-installer");
          scrollToLeadForm();
        }}
        ctaName="unifi"
        page="unifi-installer"
        label="Free UniFi Consultation"
      />
    </main>
  );
}